// Fixed presets for the Trip Distribution "Profile Name" dropdown; selecting
// one samples its type and parameters below rather than calling the API.
// Distances and normal-distribution sigma are in km; lognormal sigma and GEV
// shape are dimensionless. Profile names are also persisted as dropdown values.
export const TRIP_DISTRIBUTION_PROFILES = [
  {
    id: "normal_100_narrow",
    name: "Normal 100km, narrow range",
    type: "normal",
    parameters: { mean: 100, sigma: 24, min: 50, max: 150 },
  },
  {
    id: "normal_150_wide",
    name: "Normal 150km, wide range",
    type: "normal",
    parameters: { mean: 150, sigma: 48, min: 50, max: 250 },
  },
  {
    id: "normal_250_narrow",
    name: "Normal 250km, narrow range",
    type: "normal",
    parameters: { mean: 250, sigma: 20, min: 200, max: 300 },
  },
  {
    id: "uniform_50_150",
    name: "Uniform 50-150km",
    type: "uniform",
    parameters: { min: 50, max: 150 },
  },
  {
    id: "uniform_100_300",
    name: "Uniform 100-300km",
    type: "uniform",
    parameters: { min: 100, max: 300 },
  },
  {
    id: "uniform_200_300",
    name: "Uniform 200-300km",
    type: "uniform",
    parameters: { min: 200, max: 300 },
  },
  {
    id: "triangular_100",
    name: "Triangular 100km",
    type: "triangular",
    parameters: { min: 50, mode: 100, max: 150 },
  },
  {
    id: "triangular_200",
    name: "Triangular 200km",
    type: "triangular",
    parameters: { min: 100, mode: 200, max: 300 },
  },
  {
    id: "triangular_250",
    name: "Triangular 250km",
    type: "triangular",
    parameters: { min: 125, mode: 250, max: 375 },
  },
  {
    id: "lognormal_100_smooth",
    name: "Log normal 100km smooth",
    type: "lognormal",
    parameters: { median: 90, sigma: 0.48 },
  },
  {
    id: "lognormal_100_sharp",
    name: "Log normal 100km sharp",
    type: "lognormal",
    parameters: { median: 92, sigma: 0.25 },
  },
  {
    id: "lognormal_200_sharp",
    name: "Log normal 200km sharp",
    type: "lognormal",
    parameters: { median: 180, sigma: 0.28 },
  },
  {
    id: "extreme_150_sharp",
    name: "Extreme value 150km sharp",
    type: "gev",
    parameters: { shape: 0.55, location: 145, scale: 38 },
  },
  {
    id: "extreme_300_sharp",
    name: "Extreme value 300km sharp",
    type: "gev",
    parameters: {
      shape: 0.67726853,
      location: 297.75568482,
      scale: 8.31544029,
    },
  },
  {
    id: "extreme_300_smooth",
    name: "Extreme value 300km smooth",
    type: "reverseGumbel",
    parameters: { location: 301.00135703, scale: 24.96175485 },
  },
];

/**
 * Gaussian density at distance x, centered on mean with standard deviation
 * sigma. This evaluates the unbounded normal curve; preset min/max cutoffs are
 * applied by calculateTripDistribution, not by this helper. Requires sigma > 0.
 */
export function normalPdf(x, mean, sigma) {
  return (
    Math.exp(-0.5 * Math.pow((x - mean) / sigma, 2)) /
    (sigma * Math.sqrt(2 * Math.PI))
  );
}

/**
 * Constant density within the inclusive [min, max] distance interval and zero
 * outside it. Requires max > min; the sampled endpoints are later normalized
 * as discrete chart bins rather than integrated as a continuous density.
 */
export function uniformPdf(x, min, max) {
  return x >= min && x <= max ? 1 / (max - min) : 0;
}

/**
 * Triangular density: rises linearly from min to its peak at mode, then falls
 * linearly to zero at max. Returns zero outside the interval and requires
 * min < mode < max to keep both slopes well-defined.
 */
export function triangularPdf(x, min, mode, max) {
  if (x < min || x > max) {
    return 0;
  }

  if (x <= mode) {
    return (2 * (x - min)) / ((max - min) * (mode - min));
  }

  return (2 * (max - x)) / ((max - min) * (max - mode));
}

/**
 * Lognormal density for positive distances; returns zero for x <= 0.
 * The supplied median is converted to log-space location (mu), while sigma
 * is the log-space standard deviation, not a distance in km. Requires both
 * median and sigma to be positive.
 */
export function lognormalPdf(x, median, sigma) {
  if (x <= 0) {
    return 0;
  }

  const mu = Math.log(median);

  return (
    Math.exp(-0.5 * Math.pow((Math.log(x) - mu) / sigma, 2)) /
    (x * sigma * Math.sqrt(2 * Math.PI))
  );
}

/**
 * Generalized extreme value (GEV) density. Standardizes x using location and
 * scale, then uses shape to determine the tail and the valid support q > 0.
 * Returns zero outside that support. This formula requires scale > 0 and
 * shape !== 0 (the Gumbel limit is not implemented here).
 */
export function gevPdf(x, shape, location, scale) {
  const z = (x - location) / scale;
  const q = 1 + shape * z;

  if (q <= 0) {
    return 0;
  }

  return (
    (Math.exp(-Math.pow(q, -1 / shape)) * Math.pow(q, -1 / shape - 1)) / scale
  );
}

/**
 * Reverse-Gumbel density, with its peak near location and a longer left tail.
 * Scale controls its width and must be positive; unlike the GEV helper, this
 * formula has no finite support boundary.
 */
export function reverseGumbelPdf(x, location, scale) {
  const z = (x - location) / scale;

  return Math.exp(z - Math.exp(z)) / scale;
}

/**
 * Converts nonnegative sampled densities into percentages whose sum is 100.
 * The common sample spacing cancels out, so only relative heights matter.
 * An all-zero input stays all zero instead of producing a valid distribution;
 * non-finite or negative inputs are not validated here.
 */
export function normalizeTo100(values) {
  const total = values.reduce((sum, value) => sum + value, 0);

  if (total === 0) {
    return values.map(() => 0);
  }

  return values.map((value) => (value / total) * 100);
}

/**
 * Samples a preset PDF at 0, 10, ..., 500 km, dispatching by profile type.
 * Normal presets are explicitly truncated to their min/max; the other types
 * use their own PDF support. Normalizes the 51 sampled heights to percentages
 * for the chart and persisted tripProfile (x/y) points. This is a discrete,
 * finite-range approximation: mass between samples or beyond 500 km is not
 * included. Unknown types throw; parameter validity is assumed from presets.
 */
export function calculateTripDistribution(profile) {
  const xValues = Array.from({ length: 51 }, (_, index) => index * 10);

  const rawValues = xValues.map((distance) => {
    switch (profile.type) {
      case "normal": {
        const { mean, sigma, min, max } = profile.parameters;

        if (
          min !== undefined &&
          max !== undefined &&
          (distance < min || distance > max)
        ) {
          return 0;
        }

        return normalPdf(distance, mean, sigma);
      }

      case "uniform":
        return uniformPdf(
          distance,
          profile.parameters.min,
          profile.parameters.max,
        );

      case "triangular":
        return triangularPdf(
          distance,
          profile.parameters.min,
          profile.parameters.mode,
          profile.parameters.max,
        );

      case "lognormal":
        return lognormalPdf(
          distance,
          profile.parameters.median,
          profile.parameters.sigma,
        );

      case "gev":
        return gevPdf(
          distance,
          profile.parameters.shape,
          profile.parameters.location,
          profile.parameters.scale,
        );

      case "reverseGumbel":
        return reverseGumbelPdf(
          distance,
          profile.parameters.location,
          profile.parameters.scale,
        );

      default:
        throw new Error(`Unsupported trip distribution: ${profile.type}`);
    }
  });

  return {
    x: xValues,
    probability: normalizeTo100(rawValues),
  };
}
